/**
 * 표의 «값 없음» 처리 — 최종지시서 ㉑ §3-7 (R3).
 *
 * 규칙:
 *   ① 한 열이 «모든 행에서» 비면 그 열을 통째로 뺀다(머리 + 칸) — `visibleColumns`.
 *   ② 일부 행만 비면 칸에 「—」 한 글자를 둔다 — `<NullCell />`. (RD-3 C4: 종전 sr-only 「해당 없음」은 G22 금지 문구라 폐기. 읽기 전용 보조 텍스트는 「없음」.)
 *
 * ⚠️ `.e-sr` 는 ds-e.css(@layer patterns · layout 전역 import)의 sr-only 클래스다.
 */
import type { ReactNode } from 'react';

export function NullCell() {
  return (
    <>
      <span aria-hidden="true">—</span>
      <span className="e-sr">없음</span>
    </>
  );
}

/** 칸 내용 — null 이면 「—」. */
export function cellOrNull(v: ReactNode | null | undefined): ReactNode {
  return v === null || v === undefined || v === '' || v === false ? <NullCell /> : v;
}

/**
 * 보여 줄 열 key 목록. `cells(row)` 는 열 key → 칸 값(null = 없음)을 준다.
 * 어떤 행에서든 null 이 아닌 값이 하나라도 있는 열만 남긴다(입력 순서 유지).
 */
export function visibleColumns<K extends string, R>(keys: readonly K[], rows: readonly R[], cells: (row: R) => Partial<Record<K, unknown>>): K[] {
  const present = new Set<K>();
  for (const r of rows) {
    const c = cells(r);
    for (const k of keys) {
      const v = c[k];
      if (v !== null && v !== undefined && v !== '' && v !== false) present.add(k);
    }
  }
  return keys.filter((k) => present.has(k));
}
